import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const Newsletter = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = {};
    if (!name.trim()) next.name = t("newsletter.form.errors.nameRequired");
    if (!email.trim()) {
      next.email = t("newsletter.form.errors.emailRequired");
    } else if (!emailRegex.test(email.trim())) {
      next.email = t("newsletter.form.errors.emailInvalid");
    }
    if (!consent) next.consent = t("newsletter.form.errors.consentRequired");
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    // Persist lead locally (no backend). Navigate to Thank You page.
    try {
      const leads = JSON.parse(localStorage.getItem("inv_leads") || "[]");
      leads.push({ name: name.trim(), email: email.trim(), ts: new Date().toISOString() });
      localStorage.setItem("inv_leads", JSON.stringify(leads));
    } catch (err) {
      /* noop */
    }
    navigate("/thank_you");
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="newsletter-form"
      className="bg-[#0F172A] border border-slate-800 rounded-xl p-6 sm:p-8 shadow-lg"
      noValidate
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="lead-name" className="text-xs uppercase tracking-widest text-emerald-400 font-mono">
            {t("newsletter.form.nameLabel")}
          </label>
          <input
            id="lead-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("newsletter.form.namePlaceholder")}
            data-testid="lead-form-name-input"
            aria-invalid={!!errors.name}
            className="bg-[#0B1120] border border-slate-700 rounded-md px-4 py-3 text-slate-100 placeholder-slate-500 focus:border-emerald-500"
          />
          {errors.name && (
            <span data-testid="lead-form-name-error" className="text-xs text-red-400">
              {errors.name}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="lead-email" className="text-xs uppercase tracking-widest text-emerald-400 font-mono">
            {t("newsletter.form.emailLabel")}
          </label>
          <input
            id="lead-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("newsletter.form.emailPlaceholder")}
            data-testid="lead-form-email-input"
            aria-invalid={!!errors.email}
            className="bg-[#0B1120] border border-slate-700 rounded-md px-4 py-3 text-slate-100 placeholder-slate-500 focus:border-emerald-500"
          />
          {errors.email && (
            <span data-testid="lead-form-email-error" className="text-xs text-red-400">
              {errors.email}
            </span>
          )}
        </div>
      </div>

      <label className="mt-5 flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="lead-form-consent-checkbox"
          className="mt-1 h-4 w-4 rounded border-slate-600 bg-[#0B1120] text-emerald-600 focus:ring-emerald-500"
        />
        <span>{t("newsletter.form.consentLabel")}</span>
      </label>
      {errors.consent && (
        <span data-testid="lead-form-consent-error" className="block mt-1 text-xs text-red-400">
          {errors.consent}
        </span>
      )}

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          data-testid="lead-form-submit-button"
          disabled={submitting}
          className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-70"
        >
          {submitting ? t("newsletter.form.submitting") : t("newsletter.form.submit")}
        </button>
        <p className="text-xs text-slate-500 max-w-md">{t("newsletter.note")}</p>
      </div>
    </form>
  );
};

export default Newsletter;
